// Search-intent content for the public solution pages. One entry per module.
export const SOLUTIONS = {
  inventory: {
    title: "Workshop inventory software — stock, reorder lists and multi-currency costs",
    h1: "Stock control built for a workshop, not a warehouse chain",
    intent: "workshop inventory software",
    lead: "Know what is on the racking, what it cost in the currency you bought it in, and what needs ordering before the line stops.",
    points: [
      ["Add and adjust in seconds", "Book material in and out from the row itself with + and −, every move logged with who, when and why."],
      ["Low stock that finds you", "Set a reorder level per item and the reorder list writes itself — no spreadsheet to remember."],
      ["Bought abroad, costed at home", "Record the origin country and the currency you actually paid, and see it converted to your base currency."],
      ["Photos on every item", "A gallery per item so the storeman picks the right bracket first time."],
      ["Shift handover export", "One CSV of stock and movements for the next shift or the accountant."],
    ],
    faqs: [
      ["Do I need barcodes to start?", "No. Item codes and locations are enough on day one; scanning is an optimisation, not a prerequisite."],
      ["Can I hold stock in more than one currency?", "Yes — each item keeps its purchase currency and converts to your workspace base currency for reporting."],
      ["How do I stop the same variance every quarter?", "Log a cause against every adjustment. Cycle counting a small band of items each week beats one annual count."],
    ],
    related: ["tool-crib", "purchasing", "marketplace"],
  },
  "tool-crib": {
    title: "Tool crib software — tool checkout, calibration due dates and overdue returns",
    h1: "A tool crib that tells you who has the torque wrench",
    intent: "tool crib software",
    lead: "Check tools out to a name and a job, check them back in with a condition, and never let a calibration date lapse quietly.",
    points: [
      ["Checkout by person and job", "One tag, one record, one history — who took it, when, and what for."],
      ["Calibration and service dates", "Measuring kit carries its due date; overdue items surface on their own."],
      ["Overdue return chasing", "A short daily list of what has not come back, while it is still findable."],
      ["Condition on return", "Damage is attached to a person and a job while it can still be discussed."],
      ["Full per-tool history", "See which tools break, which crews break them, and when replacing beats repairing."],
    ],
    faqs: [
      ["What does an overdue calibration actually cost me?", "It can invalidate the work done with the tool. Treating the due date as data rather than memory is the whole point."],
      ["Can contractors take tools out?", "Yes — vendors and contractors can hold a checkout against their own name so the trail stays complete."],
    ],
    related: ["inventory", "safety", "purchasing"],
  },
  purchasing: {
    title: "Purchase orders and supplier requests for small manufacturers",
    h1: "From asking a price to receipting the goods, in one thread",
    intent: "purchase order software small manufacturer",
    lead: "Ask several suppliers for a price, compare what comes back, raise the order, then receipt it straight into stock.",
    points: [
      ["Request, compare, award", "Put the same request to several suppliers and compare the replies side by side."],
      ["Multi-line orders in 41 currencies", "Tax, shipping and per-line receiving, drafted to received."],
      ["Receipting adds stock", "Receive a line and the stock moves — no double entry, no forgotten delivery."],
      ["Supplier records that stay useful", "Contacts, lead times and history against the partner, not in someone's inbox."],
    ],
    faqs: [
      ["Can I part-receive an order?", "Yes — receipt line by line as the delivery arrives, and the balance stays outstanding."],
      ["Does it handle import duty and freight?", "Freight and tax sit on the order, and the shipping module prices the lane separately."],
    ],
    related: ["inventory", "freight", "finance"],
  },
  freight: {
    title: "Compare freight quotes — local and international, with landed cost",
    h1: "Compare every carrier on the lane before you book",
    intent: "compare freight quotes",
    lead: "Put in the countries, the weight and the distance and see every carrier serving that lane, priced to the total rather than the headline rate.",
    points: [
      ["Every carrier on the lane", "Base charge, weight, distance, fuel surcharge and customs, totalled."],
      ["Transit days next to the price", "Two days cheaper is not cheaper if the line stops."],
      ["Book, insure and pay", "Pick-up or drop-off, cargo cover and card payment in the same flow."],
      ["Status timeline and photos", "Pickup and delivery photos, ETA and a claim route if it goes wrong."],
    ],
    faqs: [
      ["Why is my small parcel priced the same as a heavier one?", "Minimum charge. If most of your loads are light, negotiate the minimum, not the per-kg rate."],
      ["What is volumetric weight?", "Air and parcel freight charge on actual or volumetric weight, whichever is greater. Measure the crate before accepting a quote."],
    ],
    tool: ["Try the free freight calculator", "/tools/freight-calculator"],
    related: ["logistics", "insurance", "purchasing"],
  },
  logistics: {
    title: "Fleet and dispatch software for local carriers and hauliers",
    h1: "Fleet, drivers, depots and a dispatch board that checks itself",
    intent: "dispatch software small fleet",
    lead: "Assign trips to runs with capacity and duty checks built in, then measure on-time performance and cost per kg.",
    points: [
      ["Dispatch board", "Multi-stop trips with capacity and duty-time safeguards."],
      ["Drivers and depots", "Licences, duty and depot coverage in one place."],
      ["KPIs that matter", "On-time performance, cost per kg and fleet utilisation."],
      ["Driver checks", "Daily walkaround records, including the nil-defect days."],
    ],
    faqs: [
      ["Does it handle subcontracted loads?", "Yes — carriers on the platform can quote and run jobs alongside your own fleet."],
      ["How do you work out cost per kg?", "Total run cost against the weight actually carried, per trip and per vehicle, so an under-loaded run shows up rather than hiding in the monthly fuel bill."],
      ["Can drivers record their daily checks?", "Yes — walkaround checks, including nil-defect days, are recorded against the vehicle and the driver."],
    ],
    related: ["freight", "safety", "jobs"],
  },
  safety: {
    title: "Fire, flood and earthquake safety software — plans, drills and inspections",
    h1: "The emergency plan, the drill record and the kit register in one place",
    intent: "fire drill and extinguisher inspection software",
    lead: "Publish the procedure for every hazard, log every drill and inspection, report incidents with photos, and raise an alert everyone sees instantly.",
    points: [
      ["Procedures per hazard and site", "Fire, flood, earthquake, storm, spill, gas — numbered steps and a muster point."],
      ["Drill records", "Evacuation seconds, attendance and the next due date."],
      ["Kit register", "Extinguishers, alarms, hydrants, first aid, flood pumps — with inspection dates and overdue flags."],
      ["Incidents with photos", "Reported by anyone on site, tracked to closed, with the admin emailed."],
      ["Emergency alerts", "One broadcast reaches everyone on site, contractors included."],
      ["A public safety page", "Share your procedures and muster point with visitors and contractors, no login needed."],
    ],
    faqs: [
      ["How often should we run a fire drill?", "Twice a year is a common baseline for a workshop, quarterly with night shifts or hot work, and always after a layout change."],
      ["Who can report an incident?", "Anyone on the site team. External agents and advisors see only the procedures and live alerts."],
    ],
    related: ["logistics", "tool-crib", "insurance"],
  },
  finance: {
    title: "Compare working capital and equipment finance for small manufacturers",
    h1: "Compare lenders on true cost, not the advertised rate",
    intent: "working capital loan comparison small business",
    lead: "Banks, NBFCs, microlenders and development banks by country and city, with the instalment and the true cost of credit worked out before you apply.",
    points: [
      ["Instalment and true cost", "Fees included, so the cheapest headline rate does not win by default."],
      ["13 finance types", "Working capital, equipment, invoice finance, trade credit and more."],
      ["Apply once", "One application to the lenders that fit, then compare the offers that come back."],
      ["Repay online", "Instalments and receipts in the same place as the rest of your money."],
    ],
    faqs: [
      ["What is the true cost of credit?", "Total interest plus arrangement and processing fees over the whole term — the only number worth comparing."],
      ["Do you lend money?", "No. Unity & Utility is software that connects you to lenders and runs the paperwork."],
    ],
    tool: ["Try the free finance and scheme checker", "/tools/finance-checker"],
    related: ["government-schemes", "purchasing", "insurance"],
  },
  "government-schemes": {
    title: "Government scheme eligibility for small manufacturers and MSMEs",
    h1: "Find the schemes you actually qualify for",
    intent: "government scheme eligibility small manufacturer",
    lead: "Answer six questions about the business and see which schemes you qualify for, which you nearly qualify for, and why.",
    points: [
      ["Eligibility, not a list", "Turnover, years trading, MSME status and ownership checked against each scheme."],
      ["Near misses explained", "One reason short? You get told which one."],
      ["Claim in the platform", "Apply, then a scheme officer reviews it in the same workspace."],
      ["Linked to jobs and training", "Claim apprenticeship and training support against real hires."],
    ],
    faqs: [
      ["Is the eligibility answer binding?", "No — it is indicative. The scheme officer's review is the decision."],
      ["What decides whether we qualify?", "Country, the type of funding, annual turnover, years trading, MSME status and ownership, checked against each published scheme."],
      ["Can we claim a scheme against a new hire?", "Yes — apprenticeship and training support can be linked to a real hire and claimed in the platform."],
    ],
    tool: ["Check your eligibility free", "/tools/finance-checker"],
    related: ["finance", "jobs", "legal-advice"],
  },
  insurance: {
    title: "Cargo, fleet and facility insurance for workshops and carriers",
    h1: "Cover the load, the fleet and the yard without the phone calls",
    intent: "cargo insurance for small carriers",
    lead: "Insurers by country, instant premiums, certificates as PDFs, and a claim route with photos when it goes wrong.",
    points: [
      ["Cover per shipment or per year", "Cargo, fleet, facilities, liability and local cart carriers."],
      ["Instant premium, real quote", "An indicative premium immediately, then a quote from a verified agent."],
      ["Certificates on file", "PDF certificates against the policy, ready for the customer or the bank."],
      ["Claims with evidence", "Photos, timeline and status, in the same place as the shipment."],
    ],
    faqs: [
      ["Are you an insurer?", "No. Verified insurance agents quote and underwrite; the platform runs the process."],
      ["Can I insure a single shipment?", "Yes — cargo cover can be taken per shipment at the point of booking, or annually across the fleet."],
      ["What do I need for a claim?", "Photos, the shipment or asset record and a description. Both sit against the policy so the adjuster gets the whole file at once."],
    ],
    related: ["freight", "safety", "finance"],
  },
  marketplace: {
    title: "Sell surplus stock and run sealed-bid tenders",
    h1: "Turn surplus into cash, and put work out to tender properly",
    intent: "sell surplus industrial stock online",
    lead: "Advertise stock and services to other businesses, or run a tender with sealed bids and a clean award trail.",
    points: [
      ["List from an item", "Advertise straight from a stock record, gallery and origin included."],
      ["Sealed-bid tenders", "Bids stay closed until the deadline, then you award with the trail intact."],
      ["Card payment on award", "Take payment in the platform, with a receipt."],
      ["Public shopfront", "Your listings and posts on a public page anyone can share."],
    ],
    faqs: [
      ["Who sees my listing?", "Buyers on the platform, plus the public shopfront if you publish it."],
      ["How does a sealed-bid tender work?", "Bids stay closed until the deadline, then you open them together and award, with the trail kept."],
      ["Can I sell straight from stock?", "Yes — list from a stock record and the photos, quantity and origin come with it."],
    ],
    related: ["inventory", "purchasing", "freight"],
  },
  jobs: {
    title: "Post industrial vacancies, apprenticeships and training",
    h1: "Hire for the floor and the road, and prove the training",
    intent: "post industrial jobs and apprenticeships",
    lead: "Public vacancy pages, shortlisting, interviews, apprenticeships and training records — with scheme support claimed against real hires.",
    points: [
      ["Shareable vacancy pages", "Every job gets a public page that unfurls properly on WhatsApp and LinkedIn."],
      ["Shortlist and interview", "Applications, interviews and outcomes against the vacancy."],
      ["Apprenticeships and training", "Verified providers, course records and completions."],
      ["Scheme-linked hiring", "Link a hire to a government scheme claim."],
    ],
    faqs: [
      ["Do candidates need an account?", "They can read and share the public vacancy page freely; applying creates an account."],
      ["Will the vacancy share properly on WhatsApp and LinkedIn?", "Yes — every public vacancy has its own page that unfurls with the title, location and pay."],
      ["Can we run apprenticeships through it?", "Yes — verified training providers, course records and completions sit against the person and the hire."],
    ],
    related: ["government-schemes", "professional-services", "logistics"],
  },
  "legal-advice": {
    title: "Fixed-fee legal advice for small industrial businesses",
    h1: "Ask a verified advisor, on a fixed fee you agree first",
    intent: "fixed fee legal advice for small business",
    lead: "Put the question, get an AI first pass, then a quote from a verified advisor — and pay only when you accept it.",
    points: [
      ["Verified advisors only", "Registration and admin verification before anyone can quote."],
      ["Fixed fee, agreed up front", "You see the price before the work starts."],
      ["AI first pass", "An immediate orientation on the question — not legal advice, and labelled as such."],
      ["Public legal guides", "Plain-English guides on the questions that come up most."],
    ],
    faqs: [
      ["Is the AI answer legal advice?", "No. It is an orientation, clearly labelled. Only the advisor's answer is advice."],
      ["When do I pay?", "Only after you accept the advisor's fixed-fee quote. Asking the question and getting the quote costs nothing."],
      ["Are the advisors checked?", "Yes — advisors register and are verified by an admin before they can quote."],
    ],
    related: ["professional-services", "government-schemes", "finance"],
  },
  "professional-services": {
    title: "Find accountants, engineers and testing labs near your site",
    h1: "The professional you need, nearest to your postcode",
    intent: "find industrial engineer accountant near me",
    lead: "Type your postal or PIN code and see verified providers ranked by how close they actually are.",
    points: [
      ["Worldwide postcode matching", "Exact code, then prefix, then city, then country."],
      ["Verified providers", "Self-registration then admin verification, with disciplines and practice data."],
      ["Quote and pay in place", "Request, quote, accept and pay a fixed fee."],
      ["Ratings from real jobs", "Reviews attached to completed work."],
    ],
    faqs: [
      ["How is 'nearest' worked out?", "Exact postal code first, then the code prefix, then the city, then the country."],
      ["What kinds of provider are listed?", "Accountants, engineers, testing and calibration labs, surveyors, health and safety consultants and other technical disciplines."],
      ["Do I pay through the platform?", "Yes — accept a fixed-fee quote and pay by card, with the receipt kept against the job."],
    ],
    related: ["legal-advice", "jobs", "insurance"],
  },
  "ai-assistant": {
    title: "AI assistant for industrial operations — briefings, copilot and copy",
    h1: "An assistant that answers from your own live data",
    intent: "ai assistant for manufacturing operations",
    lead: "A morning briefing on what needs action today, a copilot grounded in your records, and copy written for you.",
    points: [
      ["Morning briefing", "Five bullets on late jobs, unpaid money, low stock and overdue tools."],
      ["Operations copilot", "Answers from your live records, quoting real references — not guesses."],
      ["Copy written for you", "Adverts, captions, customer updates and job posts."],
      ["Photo intelligence", "Captions and descriptions from a photo off the floor."],
      ["Smart quote review", "A second opinion on a freight quote before you book it."],
    ],
    faqs: [
      ["Does it invent numbers?", "No — the copilot answers only from the data snapshot and says what is missing instead."],
      ["Can it see everything in my workspace?", "It answers from the records you already have access to; it does not reach outside your workspace."],
      ["What is the morning briefing?", "Five short bullets each morning on late jobs, unpaid money, low stock and overdue tools — only what needs action today."],
    ],
    related: ["inventory", "freight", "marketplace"],
  },
};

export const SOLUTION_ORDER = Object.keys(SOLUTIONS);
